import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import ProgressBar from "../components/ProgressBar";
import SpellIcon from "../components/SpellIcon";
import { classFor } from "../data/classes";

interface Applier {
  name: string;
  type?: string;
  icon?: string;
  count: number;
  sharePct: number;
}

interface Row {
  ability?: string;
  abilityIds?: number[];
  icon?: string;
  uptimeMs?: number;
  uptimePct?: number;
  totalCasts?: number;
  totalApplications?: number;
  appliers?: Applier[];
}

type Tab = "overall" | "bosses";

export default function DebuffUptime({ block }: { block: MetricBlock }) {
  const overallRows = (block.rows as unknown as Row[]) ?? [];
  const bossRows = (block.bossRows as unknown as Row[] | undefined) ?? [];
  const bossTotals = (block.bossTotals as
    | { totalTimeMs?: number; bossFightCount?: number }
    | undefined) ?? {};

  // Default to bosses if we have boss data (more actionable), otherwise overall.
  const [selected, setSelected] = useState<Tab>(
    bossRows.length > 0 ? "bosses" : "overall",
  );

  if (!overallRows.length && !bossRows.length) {
    return <p className="text-sm text-gray-500">No tracked debuffs.</p>;
  }

  const rows = selected === "bosses" ? bossRows : overallRows;
  const showBossTab = bossRows.length > 0;

  return (
    <div className="space-y-3">
      {showBossTab && (
        <Tabs
          selected={selected}
          onSelect={setSelected}
          bossFightCount={bossTotals.bossFightCount ?? 0}
        />
      )}
      <DebuffTable rows={rows} />
    </div>
  );
}

function Tabs({
  selected,
  onSelect,
  bossFightCount,
}: {
  selected: Tab;
  onSelect: (t: Tab) => void;
  bossFightCount: number;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "overall"}
        onClick={() => onSelect("overall")}
        label="Overall"
      />
      <Tab
        active={selected === "bosses"}
        onClick={() => onSelect("bosses")}
        label="Bosses"
        aside={bossFightCount > 0 ? `${bossFightCount} fights` : undefined}
      />
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  aside?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      <span>{label}</span>
      {aside && (
        <span className="text-[10px] tabular-nums text-gray-500">{aside}</span>
      )}
    </button>
  );
}

function DebuffTable({ rows }: { rows: Row[] }) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.uptimePct ?? 0) - (a.uptimePct ?? 0)),
    [rows],
  );

  if (!sorted.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <p className="text-sm text-gray-500">No tracked debuffs in this view.</p>
      </section>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="pb-2 pr-3 font-medium">Debuff</th>
            <th className="pb-2 pr-3 font-medium text-right">Uptime</th>
            <th className="pb-2 pr-3 font-medium w-1/3">&nbsp;</th>
            <th className="pb-2 font-medium text-right">Casts</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const pct = r.uptimePct ?? 0;
            const color =
              pct >= 95
                ? "#34d399"
                : pct >= 80
                  ? "#f0b232"
                  : pct >= 50
                    ? "#fb923c"
                    : "#ef4444";
            return (
              <tr
                key={`${r.ability}-${i}`}
                className="border-t border-border/50 hover:bg-white/5 transition-colors"
              >
                <td className="py-2 pr-3 align-top">
                  <div className="flex items-center gap-2">
                    <SpellIcon icon={r.icon} alt={r.ability ?? ""} size={20} />
                    <span className="font-semibold">{r.ability}</span>
                  </div>
                  {r.appliers && r.appliers.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {r.appliers.map((a) => (
                        <ApplierChip key={a.name} applier={a} />
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums align-middle">
                  {pct.toFixed(1)}%
                </td>
                <td className="py-2 pr-3 align-middle">
                  <ProgressBar pct={pct} color={color} />
                </td>
                <td className="py-2 text-right tabular-nums text-gray-400 align-middle">
                  {r.totalCasts ?? "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ApplierChip({ applier }: { applier: Applier }) {
  const cls = classFor(applier.type);
  return (
    <div
      className="inline-flex items-baseline gap-1.5 text-xs"
      title={`${applier.name}: ${applier.count} cast${applier.count === 1 ? "" : "s"} (${applier.sharePct.toFixed(0)}%)`}
    >
      <span className="font-medium" style={{ color: cls.color }}>
        {applier.name}
      </span>
      <span className="tabular-nums text-gray-500">
        {applier.count}
        {applier.sharePct < 100 && (
          <span className="text-gray-600">
            {" "}
            ({applier.sharePct.toFixed(0)}%)
          </span>
        )}
      </span>
    </div>
  );
}
